import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import NaturalLanguageQuery from './components/NaturalLanguageQuery';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { aiAnalysisService } from '../../services/aiAnalysisService';

const AskZentri = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Document base selection state
  const [selectedDocumentBase, setSelectedDocumentBase] = useState('all');
  const [documentBaseOptions, setDocumentBaseOptions] = useState([]);
  
  // Analysis data for context
  const [analysisData, setAnalysisData] = useState({
    documentsAnalyzed: 0,
    categories: [],
    allFindings: [],
    documentOptions: [{ value: 'all', label: 'All Documents' }]
  });

  // Load document bases and analysis data on component mount
  useEffect(() => {
    loadDocumentBases();
  }, []);

  // Load analysis data when selectedDocumentBase changes
  useEffect(() => {
    loadAnalysisData(selectedDocumentBase);
  }, [selectedDocumentBase]);

  const loadDocumentBases = async () => {
    try {
      const documentBases = await aiAnalysisService?.getDocumentBases();
      const options = [
        { value: 'all', label: 'All Document Bases' },
        ...documentBases?.map(base => ({
          value: base?.id,
          label: base?.name
        }))
      ];
      setDocumentBaseOptions(options);
    } catch (error) {
      console.error('Error loading document bases:', error);
      setDocumentBaseOptions([{ value: 'all', label: 'All Document Bases' }]);
    }
  };

  const loadAnalysisData = async (documentBaseId = 'all') => {
    try {
      setIsLoading(true);
      const data = await aiAnalysisService?.getAnalysisData(documentBaseId);
      setAnalysisData(data);
    } catch (error) {
      console.error('Error loading analysis data:', error);
      setAnalysisData({
        documentsAnalyzed: 0,
        categories: [],
        allFindings: [],
        documentOptions: [{ value: 'all', label: 'All Documents' }]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNaturalLanguageQuery = async (query, documentBaseId = null) => {
    try {
      const targetDocumentBase = documentBaseId || selectedDocumentBase;
      return await aiAnalysisService?.processNaturalLanguageQuery(query, analysisData, targetDocumentBase);
    } catch (error) {
      console.error('Error processing query:', error);
      return 'I encountered an error while processing your query. Please try asking about specific aspects of the document analysis.';
    }
  };

  // Get current document base name for display
  const getCurrentDocumentBaseName = () => {
    if (selectedDocumentBase === 'all') return 'All Document Bases';
    const selectedBase = documentBaseOptions?.find(option => option?.value === selectedDocumentBase);
    return selectedBase?.label || 'Selected Document Base';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <main className={`pt-16 transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="MessageSquare" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Ask Zentri</h1>
                <p className="text-lg text-muted-foreground">
                  AI-powered document analysis and intelligent information retrieval
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/ai-analysis-dashboard')}
                iconName="Brain"
                iconPosition="left"
                iconSize={16}
              >
                Full AI Analysis
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/document-base-management')}
                iconName="FolderOpen"
                iconPosition="left"
                iconSize={16}
              >
                Manage Documents
              </Button>
            </div>
          </div>

          {/* Document Base Selection */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Icon name="Database" size={20} className="text-primary" />
                  <h2 className="text-lg font-medium text-foreground">Select Document Base</h2>
                </div>
                <div className="w-64">
                  <Select
                    placeholder="Choose document base to query"
                    options={documentBaseOptions}
                    value={selectedDocumentBase}
                    onChange={setSelectedDocumentBase}
                  />
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{analysisData?.documentsAnalyzed}</span> documents available
              </div>
            </div>
          </div>

          {/* Ask Zentri Interface */}
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Natural Language Query Interface
                </h2>
                <p className="text-muted-foreground">
                  Ask questions about your document collection using natural language. 
                  Zentri will analyze document content and provide comprehensive answers 
                  based on the actual text within your files.
                </p>
              </div>

              <NaturalLanguageQuery 
                onQuery={handleNaturalLanguageQuery}
                isLoading={isLoading}
                selectedDocumentBase={selectedDocumentBase}
                documentBaseName={getCurrentDocumentBaseName()}
              />
            </div>
          </div>

          {/* Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Search" size={20} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-foreground">Content Analysis</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Ask Zentri reads and analyzes the full content of your documents, 
                not just titles and descriptions. Get insights from actual document text.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} className="text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-foreground">Smart Responses</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Powered by advanced AI, Zentri provides intelligent responses 
                with source citations and specific document references.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={20} className="text-purple-600" />
                </div>
                <h3 className="text-lg font-medium text-foreground">Secure & Private</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Your documents remain secure. Zentri only accesses content 
                you have permissions to view within your organization.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AskZentri;